// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import {
  CaretDown,
  ChatCircle,
  CircleNotch,
  Plus,
  Users,
  X,
} from "@phosphor-icons/react";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentsList } from "./AskV2ReviewCommentsList";
import { useAskPunttReview } from "./useAskPunttReview";
import {
  type AskV2ReviewLeftPane,
  useAskV2ReviewWorkspace,
} from "./useAskV2ReviewWorkspace";

import {
  AskPunttArtifactProvider,
  AskPunttArtworkViewer,
  type AskPunttArtifactContextValue,
  useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { AskV2Thread } from "../transport/Thread";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type ReviewVersionOption = {
  reviewId: string;
  title: string;
};

type AskV2ReviewWorkspaceProps = {
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  versionOptions?: readonly ReviewVersionOption[];
  feedPresentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  currentUserId?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  artifactContext?: AskPunttArtifactContextValue;
  /** Chat composer pinned under the left findings column only. */
  composer?: ReactNode;
};

function ReviewPaneTabs({
  leftPane,
  commentCount,
  onChange,
  "data-testid": testId = "ask-v2-review-pane-tabs",
}: {
  leftPane: AskV2ReviewLeftPane;
  commentCount: number;
  onChange: (pane: AskV2ReviewLeftPane) => void;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const tabClass = (selected: boolean) =>
    `flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${
      selected
        ? "bg-puntt-neutral-gray-3 text-puntt-neutral-gray-12"
        : "text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-12"
    }`;

  return (
    <div
      className="flex items-center rounded-full border border-puntt-neutral-gray-5 bg-base-white p-0.5"
      data-testid={testId}
      aria-label={t("askV2.review.paneTabsLabel")}
      role="tablist"
    >
      <button
        type="button"
        role="tab"
        aria-selected={leftPane === "feed"}
        className={tabClass(leftPane === "feed")}
        data-testid="ask-v2-review-tab-feed"
        onClick={() => onChange("feed")}
      >
        <Users size={16} aria-hidden="true" />
        {t("askV2.review.feedTab")}
      </button>
      <span
        aria-hidden="true"
        className="mx-0.5 h-4 w-px shrink-0 bg-puntt-neutral-gray-5"
        data-testid="ask-v2-review-pane-tabs-divider"
      />
      <button
        type="button"
        role="tab"
        aria-selected={leftPane === "comments"}
        className={tabClass(leftPane === "comments")}
        data-testid="ask-v2-review-tab-comments"
        onClick={() => onChange("comments")}
      >
        <span className="relative inline-flex shrink-0" aria-hidden="true">
          <ChatCircle size={16} />
          <Plus
            size={8}
            weight="bold"
            className="absolute -bottom-0.5 -right-0.5"
          />
        </span>
        {t("askV2.review.commentsTab", { count: commentCount })}
      </button>
    </div>
  );
}

function VersionPicker({
  review,
  options,
  onSelect,
}: {
  review: AskPunttReview;
  options: readonly ReviewVersionOption[];
  onSelect: (reviewId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const disabled = options.length <= 1;

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("askV2.review.versionPickerLabel")}
        data-testid="ask-v2-review-version-picker"
        className="flex max-w-[220px] items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 disabled:cursor-default disabled:opacity-70"
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        <span className="truncate">{review.title}</span>
        <CaretDown size={14} aria-hidden="true" />
      </button>
      {open && options.length > 1 ? (
        <ul
          className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-md border border-puntt-neutral-gray-5 bg-base-white py-1 shadow-lg"
          role="listbox"
        >
          {options.map((option) => (
            <li key={option.reviewId}>
              <button
                type="button"
                role="option"
                aria-selected={option.reviewId === review.reviewId}
                className="block w-full px-3 py-2 text-left text-[13px] hover:bg-puntt-neutral-gray-2"
                data-testid={`ask-v2-review-version-option-${option.reviewId}`}
                onClick={() => {
                  setOpen(false);
                  onSelect(option.reviewId);
                }}
              >
                {option.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function AskV2ReviewWorkspace({
  workspace,
  transport,
  versionOptions = [],
  feedPresentation = "chat",
  feedActorName,
  feedActorAvatar,
  currentUserId,
  authorNameByUserId,
  artifactContext,
  composer,
}: AskV2ReviewWorkspaceProps) {
  const { t } = useTranslation("ui");
  const reviewQuery = useAskPunttReview(workspace.reviewId);
  const review = reviewQuery.data?.review;
  const artworkFiles = useMemo(
    () => mapReviewFilesToAssets(review?.files ?? []),
    [review?.files],
  );
  const contextFiles = useMemo(
    () => mapReviewFilesToAssets(review?.contextAssets ?? []),
    [review?.contextAssets],
  );

  // why: syncing comment selection to the /comments/:id route remounts the page
  // and resets the findings list scroll position; keep selection local while open.
  const artworkFocus = useReviewArtworkFocus({
    comments: review?.comments,
    initialCommentId: workspace.commentId,
  });

  const resolvedVersionOptions = useMemo(() => {
    if (versionOptions.length > 0) return versionOptions;
    if (!review) return [];
    return [{ reviewId: review.reviewId, title: review.title }];
  }, [review, versionOptions]);

  if (reviewQuery.isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        data-testid="ask-v2-review-loading"
      >
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <div
        className="flex flex-1 items-center justify-center px-6 text-center text-sm text-puntt-neutral-gray-10"
        data-testid="ask-v2-review-error"
      >
        {t("askV2.review.loadError")}
      </div>
    );
  }

  return (
    <div
      className="flex size-full min-h-0 flex-1 overflow-hidden"
      data-testid="ask-v2-review-workspace"
      aria-label={t("askV2.review.workspaceLabel")}
    >
      <div className="ask-puntt-surface flex min-h-0 min-w-[280px] max-w-[420px] flex-[1] flex-col border-r border-puntt-neutral-gray-5">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {workspace.leftPane === "comments" ? (
            <>
              <AskV2ReviewCommentsList
                comments={review.comments}
                contextFiles={contextFiles}
                reviewFiles={artworkFiles}
                selectedCommentId={artworkFocus.selectedCommentId}
                authorNameByUserId={authorNameByUserId}
                currentUserId={currentUserId}
                currentUserName={feedActorName}
                onSelect={artworkFocus.selectComment}
                onClear={artworkFocus.clearComment}
              />
            </>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
              {artifactContext ? (
                <AskPunttArtifactProvider value={artifactContext}>
                  <AskV2Thread
                    transport={transport}
                    presentation={feedPresentation}
                    feedActorName={feedActorName}
                    feedActorAvatar={feedActorAvatar}
                    currentUserId={currentUserId}
                  />
                </AskPunttArtifactProvider>
              ) : (
                <AskV2Thread
                  transport={transport}
                  presentation={feedPresentation}
                  feedActorName={feedActorName}
                  feedActorAvatar={feedActorAvatar}
                  currentUserId={currentUserId}
                />
              )}
            </div>
          )}
        </div>
        {composer ? (
          <div className="shrink-0 p-4" data-testid="ask-v2-review-composer">
            {composer}
          </div>
        ) : null}
      </div>
      <div
        className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden bg-puntt-neutral-gray-2"
        data-testid="ask-v2-review-artwork"
        aria-label={t("askV2.review.artworkLabel")}
      >
        <div className="flex items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-2">
          <button
            type="button"
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
            data-testid="ask-v2-review-close"
            aria-label={t("askV2.review.closeLabel")}
            onClick={workspace.closeReview}
          >
            <X size={16} weight="bold" aria-hidden="true" />
          </button>
          <VersionPicker
            review={review}
            options={resolvedVersionOptions}
            onSelect={workspace.switchReview}
          />
          <ReviewPaneTabs
            data-testid="ask-v2-review-pane-tabs"
            leftPane={workspace.leftPane}
            commentCount={review.comments.filter((c) => !c.isDeleted).length}
            onChange={workspace.setLeftPane}
          />
          <div
            className="ml-auto flex items-center gap-2"
            data-testid="ask-v2-review-header-controls"
          >
            {artworkFocus.artworkHeaderControls}
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <AskPunttArtworkViewer
            ref={artworkFocus.artworkPaneRef}
            reviewId={review.reviewId}
            title={review.title}
            files={artworkFiles}
            comments={review.comments}
            activeCommentId={artworkFocus.selectedCommentId}
            onHeaderControlsChange={artworkFocus.setArtworkHeaderControls}
            onCommentClick={(comment) =>
              artworkFocus.toggleComment(comment._id)
            }
            onArtworkBackgroundClick={() => {
              if (artworkFocus.selectedCommentId != null) {
                artworkFocus.clearComment();
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
